/**
 * Please refer the following docs for more detals.
 * https://www.100ms.live/docs/javascript/v2/how--to-guides/extend-capabilities/plugins/virtual-background
 */
import { useEffect, useRef, useState } from "react";
import { HMSVirtualBackgroundTypes } from "@100mslive/hms-virtual-background";
import {
  selectIsAllowedToPublish,
  selectIsLocalVideoPluginPresent,
  selectLocalPeerRole,
  selectLocalVideoTrackID,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";
import { VirtualBackgroundIcon } from "@100mslive/react-icons";
import { Loading, Tooltip } from "@100mslive/roomkit-react";
import IconButton from "../../IconButton";
import { useIsFeatureEnabled } from "../../components/hooks/useFeatures";
import { FEATURE_LIST } from "../../common/constants";
import { VirtualBackgroundModal } from "./VirtualBackgroundModal";

export const VirtualBackground = () => {
  const pluginRef = useRef(null);
  const hmsActions = useHMSActions();
  const isAllowedToPublish = useHMSStore(selectIsAllowedToPublish);
  const role = useHMSStore(selectLocalPeerRole);
  const [isVBLoading, setIsVBLoading] = useState(false);
  const [isVBSupported, setIsVBSupported] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const localPeerVideoTrackID = useHMSStore(selectLocalVideoTrackID);
  const isVBPresent = useHMSStore(selectIsLocalVideoPluginPresent("HMSVB"));
  const isFeatureEnabled = useIsFeatureEnabled(FEATURE_LIST.VIDEO_PLUGINS);

  async function createPlugin() {
    if (!pluginRef.current) {
      const { HMSVBPlugin } = await import("@100mslive/hms-virtual-background");
      pluginRef.current = new HMSVBPlugin(
        HMSVirtualBackgroundTypes.NONE,
        HMSVirtualBackgroundTypes.NONE
      );
    }
  }

  useEffect(() => {
    if (!localPeerVideoTrackID) {
      return;
    }
    createPlugin().then(() => {
      //check support of plugin
      const pluginSupport = hmsActions.validateVideoPluginSupport(
        pluginRef.current
      );
      setIsVBSupported(pluginSupport.isSupported);
    });
  }, [hmsActions, localPeerVideoTrackID]);

  async function addPluginToTrack() {
    if (!pluginRef.current) {
      await createPlugin();
    }
    setIsVBLoading(true);
    try {
      window.HMS.virtualBackground = pluginRef.current;
      // Note: Once the plugin is added to the track, it will start processing video frames
      // continuously. This may create blob URLs in the network tab as the plugin processes
      // each frame to apply the virtual background effect. This is expected behavior.
      await hmsActions.addPluginToVideoTrack(
        pluginRef.current,
        Math.floor(role.publishParams.video.frameRate / 2)
      );
    } catch (err) {
      console.error("add virtual background plugin failed", err);
    }
    setIsVBLoading(false);
  }

  async function removePlugin() {
    if (pluginRef.current) {
      try {
        await hmsActions.removePluginFromVideoTrack(pluginRef.current);
        pluginRef.current = null;
        window.HMS.virtualBackground = null;
      } catch (err) {
        console.error("remove virtual background failed", err);
      }
    }
  }

  const handleClick = async () => {
    if (isVBPresent) {
      // If plugin is already active, show modal to change background
      setShowModal(true);
    } else {
      // If plugin is not active, ensure it's created and added to track
      if (!pluginRef.current) {
        await createPlugin();
      }
      await addPluginToTrack();
      // Wait a bit for plugin to be ready
      setTimeout(() => {
        setShowModal(true);
      }, 100);
    }
  };

  const handleBackgroundApplied = () => {
    // Background was applied successfully
    // The modal will close itself
  };

  if (!isAllowedToPublish.video || !isVBSupported || !isFeatureEnabled) {
    return null;
  }

  return (
    <>
      <Tooltip
        title={
          isVBLoading
            ? "Adding virtual background"
            : isVBPresent
            ? "Change virtual background"
            : "Add virtual background"
        }
      >
        <IconButton
          active={isVBPresent}
          disabled={isVBLoading}
          onClick={handleClick}
          data-testid="virtual_bg_btn"
        >
          {isVBLoading ? <Loading /> : <VirtualBackgroundIcon />}
        </IconButton>
      </Tooltip>
      {showModal && (
        <VirtualBackgroundModal
          open={showModal}
          onOpenChange={setShowModal}
          pluginRef={pluginRef}
          hmsActions={hmsActions}
          role={role}
          onBackgroundApplied={handleBackgroundApplied}
          onRemoveBackground={removePlugin}
        />
      )}
    </>
  );
};
